import React from 'react';
import styled from 'astroturf/react';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import { Icon } from 'js/design_system/Icon';
import routes from 'js/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-12, 12px);
  padding: var(--spacing-md-24, 24px);
  background: var(--background-white, #fff);
  border-radius: var(--modal-corner-radius, 8px);
  box-shadow:
    0px 24px 38px 0px rgba(34, 34, 51, 0.14),
    0px 9px 46px 0px rgba(34, 34, 51, 0.12),
    0px 11px 15px 0px rgba(34, 34, 51, 0.2);
  max-width: 500px;
  position: relative;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MainContent = styled.div`
  display: flex;
  gap: var(--spacing-sm-24, 24px);
  align-items: flex-start;
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: var(--type-sans-serif-sm-size, 24px);
  font-weight: var(--type-sans-serif-lg-font-weight, 400);
  line-height: var(--type-sans-serif-lg-line-height, 38px);
  color: var(--text-primary, #223);
`;

const Description = styled.p`
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  font-size: var(--type-body-md-size, 16px);
  font-weight: var(--type-body-lg-font-weight, 400);
  line-height: var(--type-body-lg-line-height, 32px);
  color: var(--text-secondary, #444340);
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary, #223);

  &:hover {
    opacity: 0.7;
  }
`;

const SphereChartImage = styled.img`
  width: 84px;
  height: 84px;
`;

type PaperClaimedToastProps = {
  claimedCount: number;
  onClose?: () => void;
  description?: string;
  showProfileLink?: boolean;
};

export const PaperClaimedToast: React.FC<PaperClaimedToastProps> = ({
  claimedCount,
  onClose,
  description = "You'll now receive more accurate Mentions and paper suggestions.",
  showProfileLink = true,
}) => {
  const paperText = claimedCount === 1 ? 'paper' : 'papers';
  const profileUrl = currentUser
    ? routes.new_profile_url({
        page_name: currentUser.page_name,
        subdomain: currentUser.domain_name,
      })
    : '/';

  return (
    <ToastContainer>
      {onClose && (
        <CloseButton onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </CloseButton>
      )}
      <ContentWrapper>
        <MainContent>
          <IconContainer>
            <SphereChartImage
              src="/images/line_illustrations/transparent_v2/sphere_chart.svg"
              alt=""
            />
          </IconContainer>
          <TextContent>
            <Title>
              You've claimed {claimedCount} {paperText}!
            </Title>
            <Description>{description}</Description>
          </TextContent>
        </MainContent>
        {showProfileLink && (
          <LinkContainer>
            <TextButtonLink href={profileUrl} iconRight="arrow_forward">
              View on your profile
            </TextButtonLink>
          </LinkContainer>
        )}
      </ContentWrapper>
    </ToastContainer>
  );
};

type ToastRenderer = ((
  _id: string,
  closeToast?: () => void,
) => React.ReactElement) & {
  displayName?: string;
};

type RenderPaperClaimedToastOptions = {
  description?: string;
  showProfileLink?: boolean;
};

export const renderPaperClaimedToast = (
  claimedCount: number,
  options: RenderPaperClaimedToastOptions = {},
): ToastRenderer => {
  const ToastRenderer: ToastRenderer = (
    _id: string,
    closeToast?: () => void,
  ) => (
    <PaperClaimedToast
      claimedCount={claimedCount}
      onClose={closeToast}
      {...options}
    />
  );
  ToastRenderer.displayName = 'PaperClaimedToastRenderer';
  return ToastRenderer;
};
